/*
 *  This file file-upload.service.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2022
 *  (C) 2015-2022. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 *
 */

import { Injectable } from '@angular/core';
import { HttpService } from './http-service';
import { HroneSnackbarService } from 'src/app/hrone-ui-library/hrone-snackbar/hrone-snackbar.service';
import { contentTypes } from '../models/hrone-file.model';
import { PROCESS_FOLDER_IDS, TFileDownloadVirtualUrl } from '../models/common.models';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { FileUploadWithFolderIdPayload, FileUploadWithFolderIdResponse } from '../components/file/file-attacher/file-attacher.component';
import { SharedService } from './shared.service';
import { switchMap, catchError, map } from 'rxjs/operators';

// eslint-disable-next-line @typescript-eslint/no-require-imports -- @types/mime describes the v1 API (lookup) but the installed mime@2 package exposes getType(); a static import would type-error against the mismatched declaration file
const mime = require('mime/lite');

export type UploadFileApiType = 'file' | 'userFile' | 'image' | 'compressImage';

export interface UploadFileModel {
    apiType?: UploadFileApiType;
    File: File;
    FolderName?: string;  // if apiType == file || apiType ==  image || apiType ==  compressImage
    FileExtensionsAllow?: number;   // if apiType == file || apiType ==  userFile
    EmployeeCode?: string;    // if apiType == userFile
    ModuleName?: string;       // if apiType == userFile
    ProcessName?: string;     // if apiType == userFile
    CompressRatio?: number;   // if  apiType ==  compressImage
    FileMaxSize?: string;
    FolderId?: number;
    isResize?: boolean;
}

export interface FileResponseModel {
    fileName: string;
    fileDbName: string;
    resizedImageDbFileName: string;
    folderName: string;
}

export const fileUrls = {
    fileVirtualUrl: '/api/core/File/FileVirtualUrl?fileDbName=',
    fileUpload: '/api/core/File/Upload',
    fileUploadUser: '/api/core/File/UploadUserFile',
    imageResizeUpload: '/api/core/file/UploadResizeImage',
    download: '/api/core/file/Download?fileDbName=',
    compressImage: '/api/core/file/UploadCustomImage',
    trimedFileVirtualUrl: '/api/core/file/UrlPathEncode/FileVirtualUrl',
    fileUploadUrl: '/api/File/UploadUserFile'

};

export type FileType = 'image' | 'video' | 'audio' | 'file';

interface FileValidationRule {
    size: number;
    types: string[];
    sizeMessage: string;
    typeMessage: string;
}

/** Named aliases for the `FileExtensionsAllow` keys in {@link FileUploadService.fileValidation}. */
export enum EFileExtensionsAllow {
    ResizeImage = 0,
    ImageOnly = 1,
    PdfDocTxt = 2,
    PdfDocExcelPng = 3,
    PdfDocExcelImageZip = 4,
    ExcelXlsxOnly = 5,
    PdfDocTxtExcelImage = 6,
    PdfDocImage = 7,
    PdfOnly = 8,
    DocExcelImage = 9,
    PdfDocExcel = 10,
    ExcelOnly = 11,
    DocOnly = 12,
    DocPdfHtml = 13,
    DocPdfImageZip = 14,
    ZipRar = 15,
    CertPem = 16,
    PdfDocExcelImageZip10MB = 17,
    PemCrtCerP12PfxKey = 18,
    PemKey = 19
}

@Injectable()
export class FileUploadService {
    private readonly fileTypeIcons: Record<string, string> = {
        'pdf': 'fa-solid fa-file-pdf errorIcon',
        'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'fa-solid fa-file-excel primaryTxt',
        'vnd.openxmlformats-officedocument.wordprocessingml.document': 'fa-solid fa-file-word primaryTxt',
        'zip': 'fa-solid fa-file-zip primaryTxt',
        'default': 'fa-solid fa-file'
    };
    fileSize = '5MB';

    fileValidation: Record<string, FileValidationRule> = {
        0: {                  // for resize image upload
            size: 5242880,
            types: ['png', 'jpg', 'jpeg', 'bmp'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are jpeg, jpg, png, bmp'
        },
        1: {
            size: 5242880,
            types: ['jpeg', 'jpg', 'png', 'bmp'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are jpeg, jpg, png, bmp'
        },
        2: {
            size: 5242880,
            types: ['pdf', 'doc', 'docx', 'txt'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are pdf, doc, docx, txt'
        },
        3: {
            size: 5242880,
            types: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are pdf, doc, docx, xls, xlsx, png'
        },
        4: {
            size: 5242880,
            types: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpeg', 'jpg', 'png', 'bmp', 'zip', 'rar'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are pdf, doc, docx, xls, xlsx, jpeg, jpg, png, bmp, zip, rar'
        },
        5: {
            size: 5242880,
            types: ['xlsx'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File format allowed is xlsx '
        },
        6: {
            size: 5242880,
            types: ['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'jpeg', 'jpg', 'png', 'bmp'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are pdf, doc, docx, txt, xls, xlsx, jpeg, jpg, png, bmp'
        },
        7: {
            size: 5242880,
            types: ['pdf', 'doc', 'docx', 'jpeg', 'jpg', 'png', 'bmp'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are pdf, doc, docx, jpeg, jpg, png, bmp '
        },
        8: {
            size: 5242880,
            types: ['pdf'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'Only PDF files allowed'
        },
        9: {
            size: 5242880,
            types: ['doc', 'docx', 'xls', 'xlsx', 'jpeg', 'jpg', 'png', 'bmp'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are doc, docx, xls, xlsx, jpeg, jpg, png, bmp '
        },
        10: {
            size: 5242880,
            types: ['pdf', 'doc', 'docx', 'xls', 'xlsx'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are pdf, doc, docx, xls, xlsx '
        },
        11: {
            size: 5242880,
            types: ['xls', 'xlsx'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are xls, xlsx'
        },
        12: {
            size: 5242880,
            types: ['doc', 'docx'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are doc, docx'
        },
        13: {
            size: 5242880,
            types: ['doc', 'docx', 'txt', 'rtf', 'pdf', 'html', 'htm'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed are doc, docx, txt, rtf, pdf, html, htm'
        },

        14: {
            size: 5242880,
            types: ['doc', 'docx', 'pdf', 'jpeg', 'jpg', 'png', 'bmp', 'rar', 'zip'],
            sizeMessage: 'Maximum file allowed size is 5MB',
            typeMessage: 'File formats allowed  are doc, docx, pdf, jpeg, jpg, png, bmp, rar, zip'
        },
        15: {
            size: 20971520,
            types: ['zip', 'rar'],
            sizeMessage: 'Maximum file allowed size is 20MB',
            typeMessage: 'File formats allowed are zip, rar'
        },
        16: {
            size: 20971520,
            types: ['pem', 'cert'],
            sizeMessage: 'Maximum file allowed size is 20MB',
            typeMessage: 'File formats allowed  pem , cert'
        },
        17: {
            size: 10485760,
            types: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpeg', 'jpg', 'png', 'bmp', 'zip', 'rar'],
            sizeMessage: 'Maximum file allowed size is 10MB',
            typeMessage: 'File formats allowed are pdf, doc, docx, xls, xlsx, jpeg, jpg, png, bmp, zip, rar'
        },
        18: {
            size: 10485760,
            types: ['pem', 'crt', 'cer', 'p12', 'pfx', 'key'],
            sizeMessage: 'Maximum file allowed size is 20MB',
            typeMessage: 'File formats allowed are pem, crt, cer, p12, pfx, key'
        },
        19: {
            size: 10485760,
            types: ['pem', 'key'],
            sizeMessage: 'Maximum file allowed size is 20MB',
            typeMessage: 'File formats allowed are pem, key'
        }
    };

    constructor(
        private readonly _httpService: HttpService,
        public _snackBar: HroneSnackbarService,
        private readonly _shared: SharedService,
    ) { }

    convertByteTo(bytes: number) {
        if (bytes === 0) {
            return '0 B';
        }
        const k = 1000,
            dm = 3,
            sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
            i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];

    }

    getFileValidation(file: File | undefined, key: number | string) {
        const fileSize = this.fileValidation[key].size;
        const fileTypes = this.fileValidation[key].types;

        // check extention
        const ext = this.getFileExtention(file!.name).toLowerCase();
        if (fileTypes.indexOf(ext) < 0) {
            this._snackBar.Alert(this.fileValidation[key].typeMessage, '3');
            return false;
        }
        // end ext

        // size validation
        if (file!.size > fileSize) {
            this._snackBar.Alert(this.fileValidation[key].sizeMessage, '3');
            return false;
        }
        // end size

        return true;

    }

    getFileTypeValidation(file: File | undefined, key: number | string) {
        const fileTypes = this.fileValidation[key].types;

        // check extention
        const ext = this.getFileExtention(file!.name).toLowerCase();
        if (fileTypes.indexOf(ext) < 0) {
            this._snackBar.Alert(this.fileValidation[key].typeMessage, '3');
            return false;
        }
        // end ext

        return true;

    }

    /** Comma-separated `.ext` list for `key`, suitable for `app-hrone-fileupload`'s `accept` input. */
    getAcceptString(key: EFileExtensionsAllow | number): string {
        const rule = this.fileValidation[key];
        return rule ? rule.types.map(ext => `.${ext}`).join(',') : '';
    }

    /** Max upload size in MB for `key`, suitable for `app-hrone-fileupload`'s `maxSizeMB` input. */
    getMaxSizeMB(key: EFileExtensionsAllow | number): number {
        const rule = this.fileValidation[key];
        return rule ? rule.size / (1024 * 1024) : 5;
    }

    getFileExtention(name: string | null | undefined): string {
        try {
            if (name) {
                return (name.split('.').pop() ?? '').toLowerCase();
            }
        } catch {
            /* ignore */
        }

        return name ?? '';
    }

    uploadFileWithFolderId(
        payload: FileUploadWithFolderIdPayload,
        ignoreSize = false,
        uploadForEmployeeCode: string | null = null
    ): Observable<FileUploadWithFolderIdResponse> {
        const flag = ignoreSize
            ? this.getFileTypeValidation(payload.File!, payload.FileExtensionsAllow)
            : this.getFileValidation(payload.File!, payload.FileExtensionsAllow);
        if (!flag) return throwError(() => new Error('Invalid file type or size'));
        const formData = new FormData();
        formData.append('File', payload.File!);
        formData.append('FolderId', payload.FolderId.toString());
        formData.append('EmployeeCode', uploadForEmployeeCode ?? this._shared.logOnUserDetails.employeeCode);
        return this._httpService.PostFileUpload(fileUrls.fileUploadUrl, formData);
    }


    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- default kept for backward compatibility: dozens of call sites rely on the untyped default response shape
    uploadFile<T = any>(payload: UploadFileModel, ignoreSize?: boolean): Promise<T> {
        return new Promise((resolve, reject) => {
            if (payload?.FolderId) {
                let flag;
                const fileExtensionKey = payload.FileExtensionsAllow ? payload.FileExtensionsAllow : 0
                if (ignoreSize) {
                    flag = this.getFileTypeValidation(payload.File, fileExtensionKey);
                } else {
                    flag = this.getFileValidation(payload.File, fileExtensionKey);
                }
                if (flag) {
                    const formData = new FormData();
                    formData.append('File', payload.File);
                    if (payload.FolderId === 33) {
                        formData.append('emp_folder', 'true');
                        formData.append('random_folder', 'true');
                        formData.append('folder_name', 'request/resume');
                    } else {
                        formData.append('EmployeeCode', payload.EmployeeCode ?? '');
                    }
                    formData.append('FolderId', payload.FolderId.toString());

                    this._httpService.PostFileUpload(fileUrls.fileUploadUrl, formData)
                        .subscribe(
                            (response) => {
                                resolve(response);
                            },
                            (err) => {
                                if (err.error?.File?.[0]) {
                                    reject(new Error(err.error?.File?.[0]))
                                } else {
                                    reject(new Error("Whoops server error!"));
                                }
                            });
                } else {
                    reject(new Error("Whoops client error!"));
                }
            } else {
                const fileExtensionKey = payload.FileExtensionsAllow ? payload.FileExtensionsAllow : 0;
                let flag;
                if (ignoreSize) {
                    flag = this.getFileTypeValidation(payload.File, fileExtensionKey);
                } else {
                    flag = this.getFileValidation(payload.File, fileExtensionKey);
                }

                if (flag) {

                    const formData = new FormData();
                    formData.append('File', payload.File);
                    formData.append('FolderName', payload.FolderName ?? '');
                    let url = fileUrls.imageResizeUpload;
                    if (payload.apiType === 'compressImage') {
                        url = fileUrls.compressImage;
                        formData.append('CompressionRatio', payload.CompressRatio ? String(payload.CompressRatio) : '50');
                    } else if (payload.apiType === 'file') {
                        url = fileUrls.fileUpload;
                        formData.append('FileExtensionsAllow', String(payload.FileExtensionsAllow));
                        if (payload.FileMaxSize) {
                            formData.append('FileMaxSize', payload.FileMaxSize);
                        }
                    } else if (payload.apiType === 'userFile') {
                        url = fileUrls.fileUploadUser;
                        formData.append('FileExtensionsAllow', String(payload.FileExtensionsAllow));
                        formData.append('ModuleName', payload.ModuleName ? payload.ModuleName : '');
                        formData.append('ProcessName', payload.ProcessName ? payload.ProcessName : '');
                        formData.append('EmployeeCode', payload.EmployeeCode ? payload.EmployeeCode : '');
                    }

                    this._httpService.PostFile(url, formData)
                        .subscribe(
                            (response) => {
                                resolve(response);
                            },
                            err => {
                                reject(err?.error ?? "Whoops client error!");
                            });
                } else {
                    reject(new Error("Whoops client error!"));
                }
            }
        });
    }

    getFileVirtualPath(fileDbName: string | null | undefined): Promise<string> {
        return new Promise((resolve, reject) => {
            if (fileDbName) {
                const query = encodeURIComponent(fileDbName);
                this.useInternalUrl(query, resolve, reject);
            } else {
                reject(new Error("Whoops client error!"));
            }
        });
    }

    private useInternalUrl(query: string, resolve: (value: string | PromiseLike<string>) => void, reject: (reason?: unknown) => void) {
        const url = fileUrls.fileVirtualUrl + query + "&requestSource=null";
        this._httpService.Get(url).subscribe(
            (resp: TFileDownloadVirtualUrl) => {
                if (resp && resp.fileVirtualPath) {
                    resolve(resp.fileVirtualPath);
                } else {
                    reject(new Error("Whoops server error!"));
                }
            }, () => {
                reject(new Error("Whoops client error!"));
            });
    }

    getTrimedFileVirtualPath(fileDbName: string): Promise<string> {
        return new Promise((resolve, reject) => {
            if (fileDbName) {
                const url = fileUrls.trimedFileVirtualUrl + `?fileDbName=${fileDbName}` + "&requestSource=null";
                this._httpService.Get(url).subscribe(
                    (resp: TFileDownloadVirtualUrl) => {
                        if (resp && resp.fileVirtualPath) {
                            resolve(resp.fileVirtualPath);
                        } else {
                            reject(new Error("Whoops server error!"));
                        }
                    }, () => {
                        reject(new Error("Whoops client error!"));
                    });
            } else {
                reject(new Error("Whoops client error!"));
            }
        });
    }

    getBlob(base64String: string, fileName: string) {
        const contentType = this.getContentType(fileName);
        const blob = this.b64toBlob(base64String, contentType);
        return blob;
    }

    b64toBlob(b64Data: string, contentType = '', sliceSize = 512) {
        const byteCharacters = atob(b64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    }

    getContentType(fileName: string) {
        const ext = this.getFileExtention(fileName) as keyof typeof contentTypes;
        if (contentTypes[ext]) {
            return contentTypes[ext];
        }
        return "text/plain";
    }

    isImage(fileName: string) {
        const ext = this.getFileExtention(fileName);
        const list = this.fileValidation['0'].types;
        if (list.indexOf(ext) >= 0) {
            return true;
        } else {
            return false;
        }
    }

    isFileViewable(fileName: string) {
        const ext = this.getFileExtention(fileName);
        const list = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];
        if (list.indexOf(ext) >= 0) {
            return true;
        } else {
            return false;
        }
    }

    downloadFile(path: string | null | undefined) {
        if (path) {
            fetch(path).then((response) => {
                return response.blob()
            }).then(blob => {
                const blobURL = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobURL;
                const index = path.indexOf('?');
                const arr = path.slice(0, index).split('/');
                a.download = arr[arr.length - 1];
                a.target = '_blank';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(blobURL);
            });
        }
    }

    getFileType(fileName: string) {
        let val: FileType = 'file';
        const type = mime.getType(fileName);
        if (type) {
            const t = type.split('/')[0];
            if (t == 'image') {
                val = 'image';
            } else if (t == 'video') {
                val = 'video';
            } else if (t == 'audio') {
                val = 'audio';
            }
        }
        return val;
    }
    getFileIconClass(fileType: string): string {
        const extension = fileType.split('/')[1];
        return this.fileTypeIcons[extension] || this.fileTypeIcons['default'];
    }

    processHtmlAndUpload(htmlContent: string, processName: string): Observable<string> {
        if (!htmlContent) return of('');
        const folderIds = PROCESS_FOLDER_IDS[processName as keyof typeof PROCESS_FOLDER_IDS];
        if (!folderIds) {
            return throwError(() => of(`Invalid process name: ${processName}`));
        }
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        const images = Array.from(doc.querySelectorAll('img'));
        const uploadObservables = images
            .filter(img => img.src.startsWith('data:image'))
            .map(img => this.uploadImage(img.src, folderIds.imageFolderId, folderIds.takeFileDBName).pipe(map(blobUrl => ({ img, blobUrl }))));
        if (!uploadObservables.length) {
            return this.uploadHtmlFile(doc.body.innerHTML, folderIds.fileFolderId);
        }
        return forkJoin(uploadObservables).pipe(
            switchMap(results => {
                results.forEach(({ img, blobUrl }) => (img.src = blobUrl));
                return this.uploadHtmlFile(doc.body.innerHTML, folderIds.fileFolderId);
            }),
            catchError(err => {
                return throwError(() => err);
            })
        );
    }

    uploadImage(base64: string, folderId: number, takeFileDBName: boolean): Observable<string> {
        const file = this.base64ToFile(base64);
        const payload = {
            File: file,
            FolderId: folderId,
            FileExtensionsAllow: 1,
        };
        return this.uploadFileWithFolderId(payload).pipe(
            map(response => {
                return (takeFileDBName ? response.fileDbName : response.virtualPath) ?? '';
            }),
            catchError(err => {
                return throwError(() => err);
            })
        );
    }

    uploadHtmlFile(htmlContent: string, folderId: number): Observable<string> {
        const file = this.createTextFile(htmlContent);
        const payload = {
            File: file,
            FolderId: folderId,
            FileExtensionsAllow: 6,
        };
        return this.uploadFileWithFolderId(payload).pipe(
            map(res => res.fileDbName),
            catchError(err => {
                return throwError(() => err);
            })
        );
    }

    base64ToFile(base64: string): File {
        const [header, base64Data] = base64.split(',');
        const mimeMatch = header.match(/:(.*?);/);
        const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
        const extension = this.getFileExtension(mimeType);
        const byteString = atob(base64Data);
        const buffer = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            buffer[i] = byteString.charCodeAt(i);
        }
        return new File([new Blob([buffer], { type: mimeType })], `image_${Date.now()}.${extension}`, {
            lastModified: Date.now(),
            type: mimeType,
        });
    }

    getFileExtension(mimeType: string): string {
        const extensions = { 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png' };
        return extensions[mimeType as keyof typeof extensions] || 'png';
    }

    createTextFile(content: string): File {
        const utf8BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const textUint8 = new TextEncoder().encode(content);
        const combined = new Uint8Array(utf8BOM.length + textUint8.length);
        combined.set(utf8BOM, 0);
        combined.set(textUint8, utf8BOM.length);

        return new File([new Blob([combined], { type: 'text/plain' })], `file_${Date.now()}.txt`, {
            type: 'text/plain',
        });
    }

    getBlobUrl(fileDbName: string): Observable<string> {
        return from(this.getFileVirtualPath(fileDbName)).pipe(
            catchError(err => {
                return throwError(() => err);
            })
        );
    }
}
